import { Edit, PlayArrow, Stop } from '@material-ui/icons';
import Delete from '@material-ui/icons/Delete';
import { ListItemIcon, Menu, MenuItem } from '@mui/material';
import { Alert as MaterialAlert } from '@mui/material';
import Alert from 'components/Alert';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import * as React from 'react';
import { toast } from 'react-toastify';
import { WebhookStatus, WebhookStatusAlias, WebhookTypeAlias } from 'v2/interfaces/webhook2';
import webhookService from 'v2/services/webhook2';

import { useWebhookContext } from '../context';

const Actions = () => {
  const { selectedWebhook, anchorEl, isActionsOpen, setIsActionsOpen, setIsFormOpen, listWebhooks, setSelectedWebhook, setLoading } =
    useWebhookContext();

  const onFinish = () => {
    setSelectedWebhook(null);
    listWebhooks();
  };

  const onClose = () => {
    setSelectedWebhook(null);
  };

  const handleEdit = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setIsActionsOpen(false);
    setIsFormOpen(true);
  };

  const handleActive = async (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setIsActionsOpen(false);

    const diff = differenceInSeconds(new Date(), new Date(selectedWebhook.updated_at));
    if (diff < 30) {
      Alert.show({ title: 'Aguarde', message: 'Aguarde pelo menos 30s antes de poder ativar/reativar o mesmo webhook.', buttonOk: 'Aguardar' });
      onFinish();
      return;
    }

    const confirm = await Alert.confirm({
      message: (
        <>
          <p>Deseja realmente ativar esse webhook?</p>
          <p>
            <strong>URL:</strong> {selectedWebhook.url}
          </p>
          <p>
            <strong>Tipo:</strong> {WebhookTypeAlias[selectedWebhook.type]}
          </p>
        </>
      ),
      title: 'Atenção, você está ativando um webhook.',
      confirmation: true,
    });

    if (confirm) {
      try {
        setLoading(true);
        const response = await webhookService.reactive(selectedWebhook._id);

        if (response.success === true) {
          onFinish();
          toast.success('Webhook ativo com sucesso');
        } else {
          onFinish();
          toast.error('URL do webhook Inválida');
        }
      } catch (e) {
        toast.error(e);
      }
    }
    onClose();
    return;
  };

  const handleInactive = async (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setIsActionsOpen(false);

    const diff = differenceInSeconds(new Date(), new Date(selectedWebhook.updated_at));
    if (diff < 30) {
      Alert.show({ title: 'Aguarde', message: 'Aguarde pelo menos 30s antes de poder ativar/reativar o mesmo webhook.', buttonOk: 'Aguardar' });
      onFinish();
      return;
    }

    const confirm = await Alert.confirm({
      message: (
        <>
          <p>Deseja realmente desativar esse webhook?</p>

          <MaterialAlert severity='error' style={{ fontSize: 14, margin: '20px 0px' }}>
            Ao inativar sua url, todos os eventos pendentes e com erro serão arquivados.
          </MaterialAlert>

          <p>
            <strong>URL:</strong> {selectedWebhook.url}
          </p>
          <p>
            <strong>Tipo:</strong> {WebhookTypeAlias[selectedWebhook.type]}
          </p>
        </>
      ),
      title: 'Atenção, você está desativando um webhook.',
      confirmation: true,
    });

    if (confirm) {
      try {
        setLoading(true);
        await webhookService.inactive(selectedWebhook._id);
        onFinish();
        toast.success('Webhook desativado com sucesso');
      } catch (e) {
        toast.error(e);
      }
    }
    onClose();
    return;
  };

  const handleRemove = async (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setIsActionsOpen(false);
    const confirm = await Alert.confirm({
      message: (
        <>
          <p>Deseja realmente remover esse webhook?</p>
          <p>
            <strong>URL:</strong> {selectedWebhook.url}
          </p>
          <p>
            <strong>Tipo:</strong> {WebhookTypeAlias[selectedWebhook.type]}
          </p>
          <p>
            <strong>Status:</strong> {WebhookStatusAlias[selectedWebhook.state]}
          </p>
          <p>
            <strong>Atenção: </strong>ao remover o webhook todos os históricos serão removidos.
          </p>
        </>
      ),
      title: 'Atenção, você está removendo um webhook.',
      confirmation: true,
    });

    if (confirm) {
      try {
        setLoading(true);
        await webhookService.removeWebhook(selectedWebhook._id);
        toast.success('Webhook removido com sucesso');
        onFinish();
      } catch (e) {
        toast.error(e);
      }
    }
    onClose();
    return;
  };

  if (!selectedWebhook || !anchorEl) return null;

  return (
    <Menu
      anchorEl={anchorEl}
      id='account-menu'
      open={isActionsOpen}
      onClose={onClose}
      onClick={onClose}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: 'visible',
          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
          mt: 1.5,
          '& .MuiAvatar-root': {
            width: 32,
            height: 32,
            ml: -0.5,
            mr: 1,
          },
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <MenuItem onClick={handleEdit}>
        <ListItemIcon>
          <Edit />
        </ListItemIcon>
        Editar
      </MenuItem>
      {selectedWebhook?.state === WebhookStatus.inactive && (
        <MenuItem onClick={handleActive}>
          <ListItemIcon>
            <PlayArrow />
          </ListItemIcon>
          Ativar
        </MenuItem>
      )}
      {(selectedWebhook?.state === WebhookStatus.failing || selectedWebhook?.state === WebhookStatus.active) && (
        <MenuItem onClick={handleInactive}>
          <ListItemIcon>
            <Stop />
          </ListItemIcon>
          Desativar
        </MenuItem>
      )}
      <MenuItem dense onClick={handleRemove}>
        <ListItemIcon>
          <Delete color='error' fontSize='small' />
        </ListItemIcon>
        Remover
      </MenuItem>
    </Menu>
  );
};

export { Actions };
